import { AnimatePresence, motion as m } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import agencyChainSvg from '@/assets/icons/nav-chain.svg'
import settiSvg from '@/assets/icons/setti.svg'
import { env } from '@/env'
import { useGetAgencyChain } from '@/shared/hooks/queries/useGetAgencyChain'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../ui/dropdown-menu'
import { CollapsibleItem } from './collapsible-item'
import { DropdownMenuTrigger } from './dropdown-menu-trigger'

interface AgencyChainMenuOptionProps {
  opened: boolean
}

export function AgencyChainMenuOption({ opened }: AgencyChainMenuOptionProps) {
  const { t } = useTranslation('sidebar')
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { data } = useGetAgencyChain()
  const { agencyId, posId } = useParams()

  const shouldBeOpened = !agencyId && !posId && pathname.includes('agency')

  const [openMenu, setOpenMenu] = useState(
    !agencyId && !posId && pathname.includes('agency'),
  )

  useEffect(() => {
    const menuOptionsHref = [
      '/agency',
      '/agency/config',
      '/agency/details',
      '/agency/customers',
      '/agency/policies',
    ]

    const menuOptionsHrefStartsWith = ['/agency/customers', '/agency/policies']

    if (
      menuOptionsHref.includes(pathname) ||
      menuOptionsHrefStartsWith.some((href) => pathname.startsWith(href))
    ) {
      setOpenMenu(true)
    } else {
      setOpenMenu(false)
    }
  }, [pathname])

  return (
    <>
      {!opened && (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <img className="size-6" src={agencyChainSvg} alt="Agency Chains" />
          </DropdownMenuTrigger>

          <DropdownMenuContent
            side="right"
            sideOffset={24}
            align="start"
            className="bg-primaryDark border-none p-2 rounded-[5px]"
          >
            <DropdownMenuItem
              onClick={() => navigate('/agency')}
              className="px-4 text-white hover:bg-white/10 rounded-md"
            >
              {t('--menu-options-agencies')}
            </DropdownMenuItem>

            {['development', 'booking-dev'].includes(env.REACT_APP_ENV) && (
              <DropdownMenuItem
                onClick={() => navigate('/agency/customers')}
                className="px-4 text-white hover:bg-white/10 rounded-md"
              >
                {t('--menu-options-customers')}
              </DropdownMenuItem>
            )}

            <DropdownMenuItem
              onClick={() => navigate('/agency/config')}
              className="px-4 text-white hover:bg-white/10 rounded-md"
            >
              {t('--menu-options-configurations')}
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={() => navigate('/agency/details')}
              className="px-4 text-white hover:bg-white/10 rounded-md"
            >
              {t('--menu-options-details')}
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={() => navigate('/agency/policies')}
              className="px-4 text-white hover:bg-white/10 rounded-md"
            >
              {t('--menu-options-purchase-policies')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {opened && (
        <Collapsible
          defaultOpen={shouldBeOpened}
          open={openMenu}
          onOpenChange={setOpenMenu}
          className="group"
        >
          <CollapsibleTrigger className="bg-[#3E54C0] enabled:hover:brightness-110 h-10 rounded-md duration-200 w-full px-2 flex items-center gap-2 mb-0 data-[state=open]:brightness-110 ">
            <img className="size-6" src={agencyChainSvg} alt="Agency Chains" />

            <AnimatePresence>
              {opened && (
                <m.span
                  initial={{ y: 20, opacity: 0 }}
                  exit={{
                    y: 20,
                    opacity: 0,
                    transition: { duration: 0.2 },
                  }}
                  animate={{
                    y: 0,
                    opacity: 1,
                    transition: { duration: 0.2, delay: 0.2 },
                  }}
                  className="flex-1 text-left line-clamp-1"
                >
                  {data?.legalData?.name ?? 'Carregando...'}
                </m.span>
              )}
            </AnimatePresence>

            <img
              className="size-2 rotate-90 group-data-[state=open]:rotate-[270deg] duration-300"
              src={settiSvg}
              alt="Setti"
            />
          </CollapsibleTrigger>

          <CollapsibleContent className="mt-2 text-base">
            <CollapsibleItem to="/agency">
              {t('--menu-options-agencies')}
            </CollapsibleItem>

            {['development', 'booking-dev'].includes(env.REACT_APP_ENV) && (
              <CollapsibleItem
                to="/agency/customers"
                isActive={pathname.includes('customers')}
              >
                {t('--menu-options-customers')}
              </CollapsibleItem>
            )}

            <CollapsibleItem to="/agency/config">
              {t('--menu-options-configurations')}
            </CollapsibleItem>

            <CollapsibleItem to="/agency/details">
              {t('--menu-options-details')}
            </CollapsibleItem>

            <CollapsibleItem
              to="/agency/policies"
              isActive={pathname.includes('policies')}
            >
              {t('--menu-options-purchase-policies')}
            </CollapsibleItem>
          </CollapsibleContent>
        </Collapsible>
      )}
    </>
  )
}
